/**
 * Ported VERBATIM from `apr_template/src/pages/ai-analysis/RunAnalysis.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../compat/PageHeader';
import apiService from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';
import InfoTip from '../compat/InfoTip';
import {
  type AgentRun,
  createRun,
  listRuns,
  getProgress,
  cancelRun,
  deleteRun,
  resumeRun,
  recognizeApps,
} from '../compat/aiRunsApi';
import { listKnowledgeFiles } from '../compat/aiKnowledgeApi';
import { getAnalysisRepository } from '../compat/aiRepositoryApi';
import { useAuth } from '../compat/AuthContext';

const RunAnalysisHelp: Help = {
  title: 'Run Analysis',
  icon: '🤖',
  purpose: 'Launch and track AI-driven strategic analysis runs over your application portfolio. Each run produces per-application dispositions, savings ranges, themes, and a sequenced action plan.',
  doHere: [
    'Click <b>New Run</b>, name the run, optionally pick <b>one or more referencing documents</b> (hold ⌘ Cmd / Ctrl to select several), and choose a <b>scope</b> — pick applications yourself, or have the AI recognize the applications named in the referencing documents.',
    'Add optional context (client industry, client size) and a reference framework to guide the agents.',
    'Click <b>Launch Run</b>; watch the live progress card and open <b>View live results</b> at any point, or <b>Cancel run</b>.',
    'Review the stat strip (total / in progress / completed / failed) and the <b>Recent runs</b> table; click any row to open its results.',
  ],
  fits: 'Application Evaluation &rarr; Disposition Intelligence &rarr; Run Analysis',
  firstTime: 'Add context documents in the Knowledge Library first, then click New Run and launch your first analysis.',
  sections: [
    {
      type: 'html',
      label: 'Referencing documents',
      html: 'You can attach <b>multiple documents</b> to a run — architecture flow charts, vendor lists, contracts, scope notes. Open the <b>Documents</b> pulldown and hold <b>⌘ Cmd / Ctrl</b> to select several; the AI reads them all together as the reference context. Leave it empty to use the entire Knowledge Library.',
    },
    {
      type: 'html',
      label: 'Parent vs. child applications',
      html: 'A <b>parent</b> application is a roll-up that groups related child apps (e.g. <i>SAP ECC 6.0 — Europe</i> grouping its FI, MM, SD… modules). Parents are <b>never analyzed directly</b> and can\'t be selected — a roll-up has no single license or disposition of its own, so a disposition call on it would be meaningless. In the picker a parent is <b>greyed out</b> with an <b>expand ▸</b> control that lists its children, which <i>are</i> analyzable — select those instead. This keeps every disposition tied to a real, actionable application.',
    },
    {
      type: 'html',
      label: 'Analyzing a system as ONE unit',
      html: 'If you want a platform such as <b>SAP ECC</b>, <b>Oracle E-Business Suite</b> or <b>NetSuite</b> assessed as a <b>single unit</b> (one disposition, one savings/cost, one timeline) rather than module-by-module, model it as a <b>standalone application</b> (no children) on the Application Master Data screen. Conversely, if you register it as a <b>parent with children</b>, the analysis is done per child. Choose the modelling that matches how the client licenses and decides on the platform.',
    },
    {
      type: 'defs',
      label: 'Run statuses',
      items: [
        { term: 'Queued / Running', color: '#9a3412', def: 'The run is planning, running, or synthesizing — progress updates live.' },
        { term: 'Completed', color: '#14532d', def: 'Finished successfully; results and exports are available.' },
        { term: 'Failed / Cancelled', color: '#7f1d1d', def: 'The run errored out or was stopped before finishing.' },
      ],
    },
  ],
  links: [
    { href: '/apr/knowledge-library/architecture-process-diagrams', label: 'Knowledge Library' },
    { href: '/apr/application-evaluation/disposition-intelligence/analysis-results', label: 'Analysis Results' },
  ],
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);
const ACTIVE_STATUSES = new Set(['queued', 'planning', 'running', 'synthesizing']);

function fmtDuration(start?: string, end?: string): string {
  if (!start) return '—';
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  const secs = Math.max(0, Math.floor((endMs - startMs) / 1000));
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${m}m`;
}

function fmtCurrency(n?: number | null): string {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function statusPillClass(status: string): string {
  switch (status) {
    case 'completed': return 'rd-pill rd-pill-success';
    case 'failed': return 'rd-pill rd-pill-danger';
    case 'cancelled': return 'rd-pill rd-pill-neutral';
    case 'queued': return 'rd-pill rd-pill-info';
    default: return 'rd-pill rd-pill-warning'; // planning / running / synthesizing
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface AppOption {
  id: string;
  name: string;
  application_id_code?: string;
  l1_business_functional_area?: string;
  is_parent?: boolean;
  excluded_from_apr?: boolean;
}

interface Framework {
  id: string;
  name: string;
}

// Curated industry list for the client-industry picker.
const INDUSTRIES = [
  'Manufacturing', 'Healthcare & Life Sciences', 'Financial Services', 'Insurance',
  'Retail & Consumer Goods', 'Technology & Software', 'Telecommunications',
  'Energy & Utilities', 'Transportation & Logistics', 'Automotive', 'Aerospace & Defense',
  'Pharmaceuticals', 'Media & Entertainment', 'Professional Services', 'Public Sector / Government',
  'Education', 'Real Estate & Construction', 'Hospitality & Travel', 'Agriculture', 'Other',
];

// Standard field padding to match the Application Master Data inputs.
const RD_FIELD_STYLE: React.CSSProperties = { padding: '7px 9px' };

// Normalise a name for "did the document label differ from the app name?" comparison.
const collapseName = (s: string): string => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// ─── Multi-select pulldown (collapsed field → dropdown; ⌘/Ctrl-click to pick several) ──
function MultiSelectDropdown({ options, selected, onChange, placeholder, noun }: {
  options: Array<{ id: string; label: string; sublabel?: string }>;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  placeholder: string;
  noun: string; // e.g. "document"
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const summary = selected.size === 0
    ? placeholder
    : selected.size === 1
      ? (options.find((o) => selected.has(o.id))?.label || `1 ${noun} selected`)
      : `${selected.size} ${noun}s selected`;

  const pick = (e: React.MouseEvent, id: string) => {
    const multi = e.metaKey || e.ctrlKey;
    const next = new Set(selected);
    if (multi) {
      next.has(id) ? next.delete(id) : next.add(id); // toggle
    } else if (next.size === 1 && next.has(id)) {
      next.clear(); // click the only-selected again → deselect
    } else {
      next.clear(); next.add(id); // plain click → select just this one
    }
    onChange(next);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ ...RD_FIELD_STYLE, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid var(--apr-border)', borderRadius: 'var(--apr-radius-btn)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}
      >
        <span style={{ color: selected.size ? 'var(--apr-text-strong)' : 'var(--apr-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span style={{ marginLeft: 8, color: 'var(--apr-text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 40, top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid var(--apr-border)', borderRadius: 'var(--apr-radius-btn)', boxShadow: '0 10px 28px rgba(0,0,0,0.14)', maxHeight: 320, overflowY: 'auto' }}>
          <div style={{ position: 'sticky', top: 0, background: '#f8fafc', borderBottom: '1px solid var(--apr-border)', padding: '6px 10px', fontSize: 11, color: 'var(--apr-text-muted)', display: 'flex', justifyContent: 'space-between' }}>
            <span>⌘ / Ctrl-click to select multiple</span>
            {selected.size > 0 && <span onClick={() => onChange(new Set())} style={{ cursor: 'pointer', color: 'var(--apr-blue)', fontWeight: 600 }}>Clear</span>}
          </div>
          {options.map((o) => {
            const sel = selected.has(o.id);
            return (
              <div
                key={o.id}
                onClick={(e) => pick(e, o.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', cursor: 'pointer', fontSize: 13, background: sel ? 'var(--apr-blue-bg)' : 'transparent' }}
                onMouseEnter={(e) => { if (!sel) (e.currentTarget as HTMLElement).style.background = '#f2f6fc'; }}
                onMouseLeave={(e) => { if (!sel) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <span style={{ width: 14, color: 'var(--apr-blue)', fontWeight: 700 }}>{sel ? '✓' : ''}</span>
                <span style={{ color: 'var(--apr-text-strong)' }}>{o.label}</span>
                {o.sublabel && <span style={{ fontSize: 11, color: 'var(--apr-text-muted)' }}>· {o.sublabel}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const RunAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'Administrator';

  // Run list
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);

  // Active run being polled
  const [activeRun, setActiveRun] = useState<{ id: string; name: string; progress: number; status: string; message?: string } | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // New run modal
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Form state
  const [runName, setRunName] = useState('');
  const [runDescription, setRunDescription] = useState('');
  const [scope, setScope] = useState<'subset' | 'recognize'>('subset');
  const [appSearch, setAppSearch] = useState('');
  const [selectedAppIds, setSelectedAppIds] = useState<Set<string>>(new Set());
  const [clientName, setClientName] = useState('');
  const [clientIndustry, setClientIndustry] = useState('');
  const [clientSize, setClientSize] = useState('');
  const [selectedFrameworkId, setSelectedFrameworkId] = useState('');
  const [frameworkLabel, setFrameworkLabel] = useState('');
  const [referenceDocIds, setReferenceDocIds] = useState<Set<string>>(new Set());
  const [considerLicensingCost, setConsiderLicensingCost] = useState(false);
  const [additionalComments, setAdditionalComments] = useState('');
  // Apps whose CURRENT analysis is client-provided/consolidated — offered for skip-&-inherit.
  const [clientCoveredAppIds, setClientCoveredAppIds] = useState<Set<string>>(new Set());
  // Selecting an application means analyse it. An app already covered by a client
  // analysis is still analysed by default — the client document is extra material about
  // it, not a reason to leave it out — and the new AI analysis lands alongside the
  // client one (which stays current until someone pins a different one). Skipping is
  // available for the portfolio-wide case (fill only the gaps a client doc left), but
  // it is now an explicit choice: the old default silently dropped apps from their own
  // run, which produced runs that analysed nothing at all.
  const [skipClientCovered, setSkipClientCovered] = useState(false);

  // App + framework + document lists
  const [allApps, setAllApps] = useState<AppOption[]>([]);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [docs, setDocs] = useState<any[]>([]);

  // "Recognize applications from document" flow
  const [recognizing, setRecognizing] = useState(false);
  const [recognizeResult, setRecognizeResult] = useState<any>(null);
  // Manual-add search for applications the recognizer didn't surface.
  const [recogAddSearch, setRecogAddSearch] = useState('');
  // Which parent (roll-up) candidates are expanded to show their children.
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  // ── Fetch initial data ───────────────────────────────────────────────────

  const fetchRuns = useCallback(async () => {
    try {
      const data = await listRuns();
      setRuns(data);
      // Detect any active run
      const active = data.find((r) => ACTIVE_STATUSES.has(r.status));
      if (active && (!activeRun || activeRun.id !== active.id)) {
        setActiveRun({
          id: active.id,
          name: active.name,
          progress: Number(active.progress_pct),
          status: active.status,
          message: active.status_message ?? undefined,
        });
      }
    } catch (err) {
      console.error('Failed to load runs', err);
    } finally {
      setLoadingRuns(false);
    }
  }, [activeRun]);

  useEffect(() => {
    fetchRuns();
  }, []);

  // ── Progress polling ─────────────────────────────────────────────────────

  const pollProgress = useCallback(async (runId: string) => {
    try {
      const prog = await getProgress(runId);
      setActiveRun((prev) =>
        prev?.id === runId
          ? { ...prev, progress: prog.progress_pct, status: prog.status, message: prog.status_message ?? undefined }
          : prev
      );
      // Refresh the list row too
      setRuns((prev) =>
        prev.map((r) =>
          r.id === runId
            ? { ...r, status: prog.status, progress_pct: prog.progress_pct, status_message: prog.status_message }
            : r
        )
      );
      if (TERMINAL_STATUSES.has(prog.status)) {
        setActiveRun(null);
        // Do a full refresh so completed_at etc. are updated
        fetchRuns();
      }
    } catch {
      // swallow — transient network error
    }
  }, [fetchRuns]);

  useEffect(() => {
    if (!activeRun) return;
    if (TERMINAL_STATUSES.has(activeRun.status)) {
      setActiveRun(null);
      return;
    }
    const schedule = () => {
      pollTimerRef.current = setTimeout(async () => {
        await pollProgress(activeRun.id);
        schedule();
      }, 3000);
    };
    schedule();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [activeRun, pollProgress]);

  // ── Modal open — load apps + frameworks ──────────────────────────────────

  const openNewRunModal = async () => {
    setRunName('');
    setRunDescription('');
    setScope('subset');
    setAppSearch('');
    setSelectedAppIds(new Set());
    setClientName('');
    setClientIndustry('');
    setClientSize('');
    setSelectedFrameworkId('');
    setFrameworkLabel('');
    setReferenceDocIds(new Set());
    setRecognizeResult(null);
    setConsiderLicensingCost(false);
    setAdditionalComments('');
    setFormError('');
    setShowModal(true);

    try {
      const [appsData, fwData, docsData, repoData] = await Promise.all([
        apiService.getApplications(),
        apiService.getFrameworks(),
        listKnowledgeFiles().catch(() => []),
        getAnalysisRepository().catch(() => []),
      ]);
      setClientCoveredAppIds(new Set(
        (Array.isArray(repoData) ? repoData : [])
          .filter((r: any) => r.origin === 'client_document' || r.origin === 'consolidated')
          .map((r: any) => r.app_id)
      ));
      setAllApps(
        appsData.map((a: any) => ({
          id: a.id,
          name: a.name,
          application_id_code: a.applicationIdCode ?? a.application_id_code ?? '',
          l1_business_functional_area: a.l1BusinessFunctionalArea ?? a.l1_business_functional_area ?? '',
          is_parent: !!(a.isParent ?? a.is_parent),
          excluded_from_apr: !!(a.excludedFromApr ?? a.excluded_from_apr),
        }))
      );
      setFrameworks(fwData || []);
      // Licensing contracts are not disposition evidence — owner's ruling, 2026-08-03.
      // Filtered on the way IN, so every later use of `docs` inherits it rather than each
      // render site having to remember. The server withholds them too (see the server's
      // agents/knowledgeScope.ts): this dropdown is the polite half of the rule, not the
      // enforcement — leaving the selection empty means "use the whole library", and the
      // agents can also reach the library through their own tools.
      const visibleDocs = (Array.isArray(docsData) ? docsData : [])
        .filter((d: any) => d?.category !== 'licensing');
      setDocs(visibleDocs);
    } catch (err) {
      console.error('Failed to load apps/frameworks', err);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
  };

  // ── Framework selection helper ────────────────────────────────────────────

  const handleFrameworkChange = (fwId: string) => {
    setSelectedFrameworkId(fwId);
    const fw = frameworks.find((f) => f.id === fwId);
    setFrameworkLabel(fw ? fw.name : '');
  };

  // ── App checkbox helpers ──────────────────────────────────────────────────

  const toggleApp = (id: string) => {
    setSelectedAppIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleGroup = (ids: string[]) => {
    const allSelected = ids.every((id) => selectedAppIds.has(id));
    setSelectedAppIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  // Grouped + filtered apps for the subset picker. Parent (roll-up) applications are
  // never analyzable, so they never appear in the picker — only their children do.
  const filteredApps = allApps.filter((a) =>
    !a.is_parent && (!appSearch || a.name.toLowerCase().includes(appSearch.toLowerCase()))
  );

  const groupedApps = filteredApps.reduce<Record<string, AppOption[]>>((acc, a) => {
    const area = a.l1_business_functional_area || 'Unassigned';
    if (!acc[area]) acc[area] = [];
    acc[area].push(a);
    return acc;
  }, {});

  // Fast id → application lookup, used by the recognize review + selected-chips.
  const appById = allApps.reduce<Record<string, AppOption>>((acc, a) => { acc[a.id] = a; return acc; }, {});
  const toggleAppId = (id: string) =>
    setSelectedAppIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleExpandParent = (id: string) =>
    setExpandedParents((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // What "Select all" is allowed to take: the AUTO-MATCHED apps, and for a matched
  // parent roll-up its analyzable children (the parent itself is never analyzable).
  // Proposals are deliberately excluded — each is a set of COMPETING candidates for one
  // name in the document, so selecting them all would put rival systems in the same run.
  // Those stay a deliberate per-app choice.
  const recogSelectableIds = useMemo(() => {
    const ids: string[] = [];
    for (const m of (recognizeResult?.matched || [])) {
      if (m.is_parent) (m.children || []).forEach((k: any) => { if (!k.excluded_from_apr) ids.push(k.id); });
      else if (!m.excluded_from_apr) ids.push(m.id);
    }
    return Array.from(new Set(ids));
  }, [recognizeResult]);
  const allRecogSelected = recogSelectableIds.length > 0 && recogSelectableIds.every((id) => selectedAppIds.has(id));

  // Render one recognize candidate. Parents (roll-ups) are NOT directly selectable —
  // they're grayed out with an expand toggle that reveals their analyzable children.
  const renderCandidate = (c: any, key: string, fromLabel?: React.ReactNode): React.ReactNode => {
    if (c.is_parent) {
      const kids: any[] = c.children || [];
      const expanded = expandedParents.has(c.id);
      const allKids = kids.length > 0 && kids.every((k) => selectedAppIds.has(k.id));
      const someKids = kids.some((k) => selectedAppIds.has(k.id));
      return (
        <div key={key} style={{ padding: '4px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ width: 16, flexShrink: 0 }} />
            <div>
              <div>
                <span style={{ fontWeight: 600, color: 'var(--apr-text-muted)', marginRight: 6 }}>{c.application_id_code || ''}</span>
                <span style={{ color: 'var(--apr-text-muted)' }}>{c.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#8a6d3b', background: '#fcf3d6', border: '1px solid #f0e0a8', borderRadius: 8, padding: '1px 6px', marginLeft: 6, whiteSpace: 'nowrap' }}>PARENT · NOT ANALYZABLE</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--apr-text-muted)', marginTop: 2 }}>
                Roll-up of {kids.length} application{kids.length === 1 ? '' : 's'} — select the children to analyse instead.
              </div>
              <button type="button" onClick={() => toggleExpandParent(c.id)} style={{ marginTop: 4, background: 'none', border: 'none', color: 'var(--apr-blue)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                {expanded ? '▾ Hide children' : `▸ Show ${kids.length} child application${kids.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
          {expanded && (
            <div style={{ marginLeft: 26, marginTop: 4, borderLeft: '2px solid var(--apr-border)', paddingLeft: 8 }}>
              {kids.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={allKids}
                    ref={(el) => { if (el) el.indeterminate = someKids && !allKids; }}
                    onChange={() => setSelectedAppIds((prev) => { const n = new Set(prev); kids.forEach((k) => (allKids ? n.delete(k.id) : n.add(k.id))); return n; })}
                  />
                  <span style={{ fontWeight: 600, color: 'var(--apr-text-muted)' }}>Select all {kids.length} children</span>
                </label>
              )}
              {kids.map((k) => (
                <label key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedAppIds.has(k.id)} onChange={() => toggleAppId(k.id)} />
                  <span style={{ fontWeight: 600, color: 'var(--apr-text-strong)' }}>{k.application_id_code || ''}</span>
                  <span>{k.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (c.excluded_from_apr) {
      // Flagged "Not relevant for rationalization" on Application Master Data —
      // grayed out and NOT selectable (the run would skip it anyway).
      return (
        <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '4px 10px', fontSize: 13, opacity: 0.85 }}>
          <span style={{ width: 16, flexShrink: 0 }} />
          <span>
            <span style={{ fontWeight: 600, color: 'var(--apr-text-muted)', marginRight: 6 }}>{c.application_id_code || ''}</span>
            <span style={{ color: 'var(--apr-text-muted)' }}>{c.name}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1px 6px', marginLeft: 6, whiteSpace: 'nowrap' }}>NOT RELEVANT FOR DISPOSITION</span>
            <div style={{ fontSize: 11, color: 'var(--apr-text-muted)', marginTop: 2 }}>Marked “Not relevant for rationalization” in Application Master Data — turn that flag off there to include it.</div>
          </span>
        </div>
      );
    }
    return (
      <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '4px 10px', cursor: 'pointer', fontSize: 13 }}>
        <input type="checkbox" checked={selectedAppIds.has(c.id)} onChange={() => toggleAppId(c.id)} style={{ marginTop: 3 }} />
        <span>
          <span style={{ fontWeight: 600, color: 'var(--apr-text-strong)', marginRight: 6 }}>{c.application_id_code || ''}</span>
          <span>{c.name}</span>
          {typeof c.score === 'number' && <span style={{ fontSize: 11, color: 'var(--apr-text-muted)', marginLeft: 6 }}>{Math.round(c.score * 100)}% match</span>}
          {c.description && <span style={{ display: 'block', fontSize: 11, color: 'var(--apr-text-muted)' }}>{c.description}</span>}
          {fromLabel}
        </span>
      </label>
    );
  };
  // Manual-add candidates: any portfolio app matching the search, not already selected.
  const recogAddMatches = recogAddSearch.trim()
    ? allApps.filter((a) => {
        if (a.is_parent || a.excluded_from_apr || selectedAppIds.has(a.id)) return false;
        const q = recogAddSearch.toLowerCase();
        return a.name.toLowerCase().includes(q) || (a.application_id_code || '').toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  // ── Recognize applications from the referencing document ──────────────────
  const handleRecognize = async () => {
    if (referenceDocIds.size === 0) { setFormError('Pick at least one referencing document first.'); return; }
    setRecognizing(true); setFormError(''); setRecognizeResult(null);
    try {
      const r = await recognizeApps(Array.from(referenceDocIds));
      setRecognizeResult(r);
      // Nothing is selected for you. What the AI recognized is a proposal, not a
      // decision — the run costs money per app, so every app in it is chosen
      // deliberately (all at once, individually, or a parent's children together).
      setSelectedAppIds(new Set());
    } catch (err: any) {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to recognize applications from the document.');
    } finally {
      setRecognizing(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!runName.trim()) {
      setFormError('Run name is required.');
      return;
    }
    if (selectedAppIds.size === 0) {
      setFormError(scope === 'recognize'
        ? 'No applications selected. Pick a document, click “Recognize applications”, then confirm the matched list.'
        : 'Please select at least one application.');
      return;
    }

    // Nothing is skipped unless the user explicitly asked to skip client-covered apps.
    const skipApps = skipClientCovered
      ? Array.from(selectedAppIds).filter((id) => clientCoveredAppIds.has(id))
      : [];

    // Belt and braces: with "skip" ticked and every selected app covered, the run would
    // have nothing to do — it would complete instantly with no analysis, no executive
    // summary and no themes, discarding any instructions typed into it. Refuse it.
    if (selectedAppIds.size > 0 && skipApps.length === selectedAppIds.size) {
      setFormError(
        'Every application selected already has a client-provided or consolidated analysis, and “Skip them” is ticked — so this run would analyse nothing. Untick it to analyse them again, or select other applications.'
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        name: runName.trim(),
        description: runDescription.trim() || undefined,
        scope: 'subset', // both "selected" and "recognize" run over a confirmed subset
        applicationIds: Array.from(selectedAppIds),
        clientName: clientName.trim() || undefined,
        clientIndustry: clientIndustry.trim() || undefined,
        clientSize: clientSize || undefined,
        selectedFrameworkId: selectedFrameworkId || undefined,
        frameworkLabel: frameworkLabel || undefined,
        referenceDocumentIds: Array.from(referenceDocIds),
        considerLicensingCost,
        additionalComments: additionalComments.trim() || undefined,
        skipApps,
      };

      const newRun = await createRun(payload);

      // Update local list
      setRuns((prev) => [newRun, ...prev]);

      // Start polling this run
      setActiveRun({
        id: newRun.id,
        name: newRun.name,
        progress: Number(newRun.progress_pct),
        status: newRun.status,
        message: newRun.status_message ?? undefined,
      });

      closeModal();
      navigate(`/apr/application-evaluation/disposition-intelligence/analysis-results?runId=${newRun.id}`);
    } catch (err: any) {
      setFormError(err?.response?.data?.error || 'Failed to create run. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Cancel active run ─────────────────────────────────────────────────────

  const handleCancelActive = async () => {
    if (!activeRun) return;
    try {
      await cancelRun(activeRun.id);
      setActiveRun(null);
      fetchRuns();
    } catch (err) {
      console.error('Failed to cancel run', err);
    }
  };

  // ── Resume a cancelled/failed run ─────────────────────────────────────────
  const handleResumeRun = async (run: AgentRun) => {
    if (!window.confirm(`Continue run "${run.name}"? Applications already analyzed are kept; only the remaining ones will be processed.`)) return;
    try {
      await resumeRun(run.id);
      fetchRuns();
      navigate(`/apr/application-evaluation/disposition-intelligence/analysis-results?runId=${run.id}`);
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Failed to resume run');
    }
  };

  // ── Delete a run (Administrator only) ─────────────────────────────────────
  const handleDeleteRun = async (run: AgentRun) => {
    if (!window.confirm(`Delete run "${run.name}" and all of its results? This cannot be undone.`)) return;
    try {
      await deleteRun(run.id);
      if (activeRun?.id === run.id) setActiveRun(null);
      fetchRuns();
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Failed to delete run');
    }
  };

  // ─── Derived stats ────────────────────────────────────────────────────────

  const stats = {
    total: runs.length,
    active: runs.filter(r => ACTIVE_STATUSES.has(r.status)).length,
    completed: runs.filter(r => r.status === 'completed').length,
    failed: runs.filter(r => r.status === 'failed' || r.status === 'cancelled').length,
  };

  const progressFillClass = (status: string): string => {
    if (status === 'completed') return 'rd-mini-progress-fill completed';
    if (status === 'failed') return 'rd-mini-progress-fill failed';
    if (status === 'cancelled') return 'rd-mini-progress-fill cancelled';
    return 'rd-mini-progress-fill active';
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="page">
      <HelpPanel help={RunAnalysisHelp} />
      <PageHeader
        title="Run Analysis"
        subtitle="Run a strategic deep-dive of your application portfolio and produce executive-grade recommendations."
        actions={
          <button className="rd-btn rd-btn-primary" onClick={openNewRunModal}>
            <i className="material-icons" style={{ fontSize: 18 }}>add</i>
            New Run
          </button>
        }
      />
      {/* content is DIRECT children of .page (like Knowledge Library) — an extra
          wrapper div here is the flex child Safari refuses to stretch full-width. */}

        {/* Stat strip */}
        <div className="rd-stat-grid">
          <div className="rd-stat-card">
            <div className="rd-stat-card-icon blue">
              <i className="material-icons">history</i>
            </div>
            <div className="rd-stat-card-body">
              <div className="rd-stat-card-value">{stats.total}</div>
              <div className="rd-stat-card-label">Total runs</div>
            </div>
          </div>
          <div className="rd-stat-card">
            <div className="rd-stat-card-icon orange">
              <i className="material-icons">autorenew</i>
            </div>
            <div className="rd-stat-card-body">
              <div className="rd-stat-card-value">{stats.active}</div>
              <div className="rd-stat-card-label">In progress</div>
            </div>
          </div>
          <div className="rd-stat-card">
            <div className="rd-stat-card-icon green">
              <i className="material-icons">check_circle</i>
            </div>
            <div className="rd-stat-card-body">
              <div className="rd-stat-card-value">{stats.completed}</div>
              <div className="rd-stat-card-label">Completed</div>
            </div>
          </div>
          <div className="rd-stat-card">
            <div className="rd-stat-card-icon red">
              <i className="material-icons">error_outline</i>
            </div>
            <div className="rd-stat-card-body">
              <div className="rd-stat-card-value">{stats.failed}</div>
              <div className="rd-stat-card-label">Failed / cancelled</div>
            </div>
          </div>
        </div>

        {/* Live "active run" hero card */}
        {activeRun && !TERMINAL_STATUSES.has(activeRun.status) && (
          <div className="rd-active-run-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className="material-icons rd-spin" style={{ fontSize: 22 }}>autorenew</i>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 16, fontWeight: 600 }}>{activeRun.name}</span>
                  <span className="rd-pill">{activeRun.status.charAt(0).toUpperCase() + activeRun.status.slice(1)}</span>
                </div>
                {activeRun.message && (
                  <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>{activeRun.message}</div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {activeRun.progress.toFixed(0)}<span style={{ fontSize: 14, fontWeight: 400, opacity: 0.85 }}>%</span>
                </div>
                <div style={{ fontSize: 11, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Progress</div>
              </div>
            </div>

            <div style={{ height: 8, background: 'rgba(255,255,255,0.25)', borderRadius: 4, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${activeRun.progress}%`,
                  height: '100%',
                  background: '#ffffff',
                  borderRadius: 4,
                  transition: 'width 0.5s ease',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                className="rd-btn rd-btn-ghost"
                style={{ background: 'rgba(255,255,255,0.18)', color: '#ffffff', borderColor: 'transparent' }}
                onClick={() => navigate(`/apr/application-evaluation/disposition-intelligence/analysis-results?runId=${activeRun.id}`)}
              >
                View live results
              </button>
              <button
                type="button"
                className="rd-btn rd-btn-ghost"
                style={{ background: 'rgba(0,0,0,0.2)', color: '#ffffff', borderColor: 'transparent' }}
                onClick={handleCancelActive}
              >
                Cancel run
              </button>
            </div>
          </div>
        )}

        {/* Run history section */}
        <div className="rd-card" style={{ overflow: 'hidden', padding: 0 }}>
          <div className="rd-section-header">
            <h2 className="rd-section-title">
              <i className="material-icons" style={{ fontSize: 18, color: 'var(--apr-blue)' }}>history</i>
              Recent runs
              {runs.length > 0 && <span className="rd-section-title-badge">{runs.length}</span>}
            </h2>
          </div>

          {loadingRuns ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--apr-text-muted)' }}>
              <i className="material-icons rd-spin">refresh</i>
              <div style={{ marginTop: 8, fontSize: 13 }}>Loading runs…</div>
            </div>
          ) : runs.length === 0 ? (
            <div className="rd-empty" style={{ padding: '40px 20px' }}>
              <div className="rd-empty-icon">
                <i className="material-icons" style={{ fontSize: 48 }}>auto_awesome</i>
              </div>
              <h2 className="rd-empty-title">No runs yet</h2>
              <p className="rd-empty-body">
                Launch your first AI analysis to get per-application disposition recommendations,
                savings ranges, and a sequenced action plan.
              </p>

              <div className="rd-howto">
                <div className="rd-howto-step">
                  <span className="rd-howto-step-num">1</span>
                  <div className="rd-howto-step-title">Upload context</div>
                  <div className="rd-howto-step-body">
                    Add contracts, usage reports, or vendor proposals in the Knowledge Library.
                  </div>
                </div>
                <div className="rd-howto-step">
                  <span className="rd-howto-step-num">2</span>
                  <div className="rd-howto-step-title">Launch a run</div>
                  <div className="rd-howto-step-body">
                    Pick a scope and provide industry context. The agents handle the rest.
                  </div>
                </div>
                <div className="rd-howto-step">
                  <span className="rd-howto-step-num">3</span>
                  <div className="rd-howto-step-title">Review &amp; export</div>
                  <div className="rd-howto-step-body">
                    Override anything you disagree with; export to Excel or PDF when ready.
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="rd-btn rd-btn-primary"
                style={{ marginTop: 20 }}
                onClick={openNewRunModal}
              >
                <i className="material-icons" style={{ fontSize: 18 }}>add</i>
                Launch first run
              </button>
            </div>
          ) : (
            <table className="rd-run-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Scope</th>
                  <th>Status</th>
                  <th style={{ width: 180 }}>Progress</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th style={{ textAlign: 'right' }}>Savings (mid)</th>
                  {(isAdmin || runs.some((r) => r.status === 'cancelled' || r.status === 'failed')) && (
                    <th style={{ textAlign: 'right', width: 130 }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr
                    key={run.id}
                    onClick={() => navigate(`/apr/application-evaluation/disposition-intelligence/analysis-results?runId=${run.id}`)}
                  >
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--apr-text-strong)' }}>{run.name}</div>
                      {run.description && (
                        <div style={{ fontSize: 12, color: 'var(--apr-text-muted)', marginTop: 2 }}>
                          {run.description}
                        </div>
                      )}
                    </td>
                    <td style={{ textTransform: 'capitalize', color: 'var(--apr-text-muted)' }}>
                      {run.scope}
                    </td>
                    <td>
                      <span className={statusPillClass(run.status)}>{run.status}</span>
                    </td>
                    <td>
                      <div className="rd-mini-progress">
                        <div className="rd-mini-progress-track">
                          <div
                            className={progressFillClass(run.status)}
                            style={{ width: `${Number(run.progress_pct)}%` }}
                          />
                        </div>
                        <span className="rd-mini-progress-pct">
                          {Number(run.progress_pct).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td style={{ color: 'var(--apr-text-muted)' }}>
                      {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}
                    </td>
                    <td style={{ color: 'var(--apr-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDuration(run.started_at, run.completed_at)}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--apr-text-muted)' }}>—</td>
                    {(isAdmin || run.status === 'cancelled' || run.status === 'failed') && (
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {(run.status === 'cancelled' || run.status === 'failed') && (
                          <button
                            type="button"
                            onClick={() => handleResumeRun(run)}
                            title="Continue this run (analyzes only the remaining applications)"
                            style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginRight: 6 }}
                          >
                            ▸ Continue
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => handleDeleteRun(run)}
                            title="Delete run"
                            aria-label="Delete run"
                            style={{ border: 'none', background: 'transparent', color: '#b91c1c', cursor: 'pointer', padding: 4, fontSize: 15, lineHeight: 1 }}
                          >
                            🗑
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      {/* ── New Run Modal ────────────────────────────────────────────────── */}
      {showModal && (
        <div className="rd-modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="rd-modal" style={{ maxWidth: 760 }}>
            <div className="rd-modal-header">
              <h2 className="rd-modal-title">New Analysis Run</h2>
              <button className="rd-modal-close" onClick={closeModal} aria-label="Close">
                <i className="material-icons">close</i>
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
              <div className="rd-modal-body">

                {/* ── Section 1: Run Details ── */}
                <div className="rd-form-section">
                  <div className="rd-form-section-title">Run Details</div>

                  <div className="rd-form-group">
                    <label className="rd-label" htmlFor="run-name">
                      Run name <span style={{ color: 'var(--apr-logout-red)' }}>*</span>
                    </label>
                    <input
                      id="run-name"
                      type="text"
                      placeholder="e.g. Q2 2026 Portfolio Analysis"
                      value={runName}
                      onChange={(e) => setRunName(e.target.value)}
                      autoFocus
                      required
                      style={RD_FIELD_STYLE}
                    />
                  </div>

                  <div className="rd-form-group" style={{ marginBottom: 0 }}>
                    <label className="rd-label" htmlFor="run-desc">Description (optional)</label>
                    <textarea
                      id="run-desc"
                      rows={2}
                      placeholder="Brief context for this run…"
                      value={runDescription}
                      onChange={(e) => setRunDescription(e.target.value)}
                      style={{ resize: 'vertical', padding: '10px 12px' }}
                    />
                  </div>
                </div>

                {/* ── Referencing documents (multi-select) ── */}
                <div className="rd-form-section">
                  <div className="rd-form-section-title">
                    Referencing documents
                    <InfoTip title="Referencing documents">
                      <p style={{ marginTop: 0 }}>Optionally anchor this analysis to <strong>one or more documents</strong> from the Knowledge Library (e.g. an architecture flow chart, a vendor list, a contract, a scope document). The AI reads all selected documents together as the reference context.</p>
                      <p style={{ marginBottom: 0 }}>When documents are chosen, you can use the <strong>“Recognize applications from document”</strong> scope below to have the AI pull out the applications they name and analyse just those.</p>
                    </InfoTip>
                  </div>
                  <p className="rd-form-help" style={{ margin: '0 0 12px' }}>
                    Select any documents the analysis should reference (hold <strong>⌘ Cmd</strong> / <strong>Ctrl</strong> to pick several), or leave all unselected to use the full Knowledge Library.
                  </p>
                  <div className="rd-form-group" style={{ marginBottom: 0 }}>
                    <label className="rd-label" htmlFor="ref-docs">
                      Documents
                      {referenceDocIds.size > 0 && (
                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--apr-blue)' }}>· {referenceDocIds.size} selected</span>
                      )}
                    </label>
                    {docs.length === 0 ? (
                      <div className="rd-form-help" style={{ marginTop: 4 }}>No documents in the Knowledge Library yet — upload one there first.</div>
                    ) : (
                      <MultiSelectDropdown
                        placeholder="Select documents… (or leave empty for the full library)"
                        noun="document"
                        options={docs.map((d) => ({ id: d.id, label: d.file_name, sublabel: d.application_name || undefined }))}
                        selected={referenceDocIds}
                        onChange={(next) => {
                          setReferenceDocIds(next);
                          setRecognizeResult(null);
                          if (scope === 'recognize') setSelectedAppIds(new Set());
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* ── Consider licensing cost ── */}
                <div className="rd-form-section">
                  <div className="rd-form-section-title">
                    Licensing cost
                    <InfoTip title="Consider licensing cost">
                      <p style={{ marginTop: 0 }}>When enabled, the AI treats <strong>software licensing cost as a first-class lever</strong> — surfacing renegotiation, right-sizing, and consolidation savings on licensing, both per application and across vendor families.</p>
                      <p style={{ marginBottom: 0 }}>Leave it <strong>off (the default)</strong> if you don't want licensing economics to drive the dispositions — e.g. when you're focused on capability, fit, or risk rather than cost.</p>
                    </InfoTip>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={considerLicensingCost} onChange={(e) => setConsiderLicensingCost(e.target.checked)} />
                    <span>
                      Consider software licensing cost in this analysis
                      <span style={{ color: 'var(--apr-text-muted)', marginLeft: 6 }}>(off by default)</span>
                    </span>
                  </label>
                </div>

                {Array.from(selectedAppIds).some((id) => clientCoveredAppIds.has(id)) && (() => {
                  const coveredCount = Array.from(selectedAppIds).filter((id) => clientCoveredAppIds.has(id)).length;
                  // Every selected app is covered → with the box unticked this run would
                  // skip all of them and analyse nothing. Warn in place rather than let
                  // the user find out from an empty Analysis Results.
                  // Only a problem if the user ticked "skip" AND that covers everything.
                  const allCovered = coveredCount === selectedAppIds.size && skipClientCovered;
                  return (
                  <div style={{ marginTop: 14 }}>
                    <div className="rd-form-section-title">Client-provided analyses</div>
                    <div style={{ fontSize: 12.5, color: 'var(--apr-text-muted)', margin: '4px 0 8px' }}>
                      {coveredCount} selected application(s) already have a client-provided or consolidated analysis. They are analysed anyway — the client document is extra material about them — and the new AI analysis is added alongside the client one, which stays current until you pin a different one.
                    </div>
                    {allCovered && (
                      <div style={{ fontSize: 12.5, color: '#8a6d00', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '7px 10px', margin: '0 0 8px', lineHeight: 1.5 }}>
                        That is <strong>every application in this run</strong> — with “skip” ticked it would analyse nothing at all. Untick the box below to analyse them.
                      </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={skipClientCovered} onChange={(e) => setSkipClientCovered(e.target.checked)} />
                      <span>
                        Skip them and keep the client analysis as current
                        <span style={{ color: 'var(--apr-text-muted)', marginLeft: 6 }}>(this run leaves those applications untouched — useful to fill only the gaps a client document left)</span>
                      </span>
                    </label>
                  </div>
                  );
                })()}

                {/* ── Section 2: Scope ── */}
                <div className="rd-form-section">
                  <div className="rd-form-section-title">
                    Scope
                    <InfoTip title="Scope">
                      <p style={{ marginTop: 0 }}>Controls which applications the run analyses:</p>
                      <ul style={{ margin: '0 0 0 18px', padding: 0 }}>
                        <li><strong>Selected applications</strong> — you tick the exact apps to analyse.</li>
                        <li><strong>Recognize from document(s)</strong> — the AI reads the referencing document(s), extracts the applications they name, matches them to your portfolio, and you choose which of them the run analyses.</li>
                      </ul>
                    </InfoTip>
                  </div>

                  <div className="rd-form-group" style={{ marginBottom: 14 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <label
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                          border: `1px solid ${scope === 'subset' ? 'var(--apr-blue-light)' : 'var(--apr-border)'}`,
                          borderRadius: 'var(--apr-radius-btn)',
                          background: scope === 'subset' ? 'var(--apr-blue-bg)' : 'transparent',
                          cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        }}
                      >
                        <input type="radio" name="scope" value="subset" checked={scope === 'subset'} onChange={() => setScope('subset')} />
                        <div>
                          <div style={{ color: 'var(--apr-text-strong)' }}>Selected applications</div>
                          <div style={{ fontSize: 11, color: 'var(--apr-text-muted)', fontWeight: 400 }}>Pick the apps to analyze</div>
                        </div>
                      </label>
                      <label
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                          border: `1px solid ${scope === 'recognize' ? 'var(--apr-blue-light)' : 'var(--apr-border)'}`,
                          borderRadius: 'var(--apr-radius-btn)',
                          background: scope === 'recognize' ? 'var(--apr-blue-bg)' : 'transparent',
                          cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        }}
                      >
                        <input type="radio" name="scope" value="recognize" checked={scope === 'recognize'} onChange={() => { setScope('recognize'); setSelectedAppIds(new Set()); }} />
                        <div>
                          <div style={{ color: 'var(--apr-text-strong)' }}>Recognize from document(s)</div>
                          <div style={{ fontSize: 11, color: 'var(--apr-text-muted)', fontWeight: 400 }}>Analyze the apps named in the doc(s)</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Recognize-from-document flow */}
                  {scope === 'recognize' && (
                    <div className="rd-form-group" style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <button
                          type="button"
                          className="rd-btn rd-btn-primary"
                          onClick={handleRecognize}
                          disabled={referenceDocIds.size === 0 || recognizing}
                          style={{ fontSize: 13 }}
                        >
                          {recognizing ? 'Recognizing…' : '✦ Recognize applications'}
                        </button>
                        {referenceDocIds.size === 0 && <span style={{ fontSize: 12, color: 'var(--apr-text-muted)' }}>Pick at least one referencing document above first.</span>}
                        {recognizeResult && (
                          <span style={{ fontSize: 12, color: 'var(--apr-text-muted)' }}>
                            {recognizeResult.matched.length} auto-matched · {(recognizeResult.proposals?.length || 0)} to review · {recognizeResult.unmatched.length} not found · <strong style={{ color: 'var(--apr-blue)' }}>{selectedAppIds.size} selected</strong>
                          </span>
                        )}
                        {recognizeResult && recogSelectableIds.length > 0 && (
                          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                            <button
                              type="button"
                              className="rd-btn rd-btn-ghost"
                              style={{ fontSize: 12 }}
                              disabled={allRecogSelected}
                              onClick={() => setSelectedAppIds((prev) => new Set([...Array.from(prev), ...recogSelectableIds]))}
                              title="Select every auto-matched application (proposals still need a per-app choice)"
                            >
                              Select all {recogSelectableIds.length}
                            </button>
                            <button
                              type="button"
                              className="rd-btn rd-btn-ghost"
                              style={{ fontSize: 12 }}
                              disabled={selectedAppIds.size === 0}
                              onClick={() => setSelectedAppIds(new Set())}
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>
                      {recognizeResult && (
                        <>
                          <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--apr-border)', borderRadius: 'var(--apr-radius-btn)', padding: 6, background: '#fafbfc' }}>
                            {/* Auto-matched (high confidence) — proposed, never pre-selected */}
                            {recognizeResult.matched.length > 0 && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--apr-text-muted)', padding: '4px 10px' }}>Auto-matched</div>
                                {recognizeResult.matched.map((m: any) => renderCandidate(
                                  m, m.id,
                                  m.matched_on && collapseName(m.matched_on) !== collapseName(m.name)
                                    ? <span style={{ fontSize: 11, color: 'var(--apr-text-muted)', marginLeft: 6 }}>· from “{m.matched_on}”</span>
                                    : undefined
                                ))}
                              </div>
                            )}

                            {/* Proposals — near matches the user confirms */}
                            {(recognizeResult.proposals || []).length > 0 && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#b26a00', padding: '4px 10px' }}>Review these — which system did the document mean?</div>
                                {(recognizeResult.proposals || []).map((p: any, i: number) => (
                                  <div key={i} style={{ padding: '6px 10px 8px', borderTop: i > 0 ? '1px dashed var(--apr-border)' : 'none' }}>
                                    <div style={{ fontSize: 12, color: 'var(--apr-text-muted)', marginBottom: 4 }}>
                                      From document: <strong style={{ color: 'var(--apr-text-strong)' }}>“{p.name}”</strong>
                                    </div>
                                    {p.candidates.map((c: any) => renderCandidate(c, c.id))}
                                  </div>
                                ))}
                              </div>
                            )}

                            {recognizeResult.matched.length === 0 && (recognizeResult.proposals || []).length === 0 && (
                              <div style={{ padding: '12px 8px', color: 'var(--apr-text-muted)', fontSize: 13 }}>No portfolio applications matched the names found in the document. Add them manually below.</div>
                            )}
                          </div>

                          {recognizeResult.unmatched.length > 0 && (
                            <div className="rd-form-help" style={{ marginTop: 6 }}>
                              Not found in portfolio ({recognizeResult.unmatched.length}): {recognizeResult.unmatched.slice(0, 12).join(', ')}{recognizeResult.unmatched.length > 12 ? '…' : ''}
                            </div>
                          )}

                          {/* Manual add — pick any application the recognizer missed */}
                          <div style={{ marginTop: 10 }}>
                            <label className="rd-label" style={{ fontSize: 12 }}>Add an application manually</label>
                            <input
                              type="text"
                              placeholder="Search by name or App ID…"
                              value={recogAddSearch}
                              onChange={(e) => setRecogAddSearch(e.target.value)}
                              style={{ ...RD_FIELD_STYLE, marginBottom: 6 }}
                            />
                            {recogAddMatches.length > 0 && (
                              <div style={{ border: '1px solid var(--apr-border)', borderRadius: 'var(--apr-radius-btn)', background: '#fff', maxHeight: 160, overflowY: 'auto' }}>
                                {recogAddMatches.map((a) => (
                                  <div
                                    key={a.id}
                                    onClick={() => { toggleAppId(a.id); setRecogAddSearch(''); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}
                                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#f2f6fc'; }}
                                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                  >
                                    <span style={{ fontSize: 16, color: 'var(--apr-blue)', lineHeight: 1 }}>＋</span>
                                    <span style={{ fontWeight: 600, color: 'var(--apr-text-strong)' }}>{a.application_id_code || ''}</span>
                                    <span>{a.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Final selected set — from any source (matched / proposal / manual) */}
                          {selectedAppIds.size > 0 && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--apr-text-muted)', marginBottom: 4 }}>
                                Selected for analysis ({selectedAppIds.size})
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {Array.from(selectedAppIds).map((id) => {
                                  const a = appById[id];
                                  if (!a) return null;
                                  return (
                                    <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--apr-blue-bg)', border: '1px solid var(--apr-blue-light)', borderRadius: 12, padding: '2px 8px', fontSize: 12 }}>
                                      <span style={{ fontWeight: 600 }}>{a.application_id_code || ''}</span>
                                      <span style={{ color: 'var(--apr-text-strong)' }}>{a.name}</span>
                                      <span onClick={() => toggleAppId(id)} style={{ cursor: 'pointer', color: 'var(--apr-text-muted)', fontWeight: 700 }} title="Remove">×</span>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* App selector for subset */}
                  {scope === 'subset' && (
                    <div className="rd-form-group" style={{ marginBottom: 0 }}>
                      <label className="rd-label">
                        Applications
                        {selectedAppIds.size > 0 && (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--apr-blue)' }}>
                            · {selectedAppIds.size} selected
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        placeholder="Search applications…"
                        value={appSearch}
                        onChange={(e) => setAppSearch(e.target.value)}
                        style={{ ...RD_FIELD_STYLE, marginBottom: 10 }}
                      />
                      <div
                        style={{
                          maxHeight: 280,
                          overflowY: 'auto',
                          border: '1px solid var(--apr-border)',
                          borderRadius: 'var(--apr-radius-btn)',
                          padding: 6,
                          background: '#fafbfc',
                        }}
                      >
                        {Object.keys(groupedApps).length === 0 ? (
                          <div style={{ padding: '16px 8px', color: 'var(--apr-text-muted)', fontSize: 13, textAlign: 'center' }}>
                            No applications found.
                          </div>
                        ) : (
                          Object.entries(groupedApps)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([area, apps]) => {
                              // "Select all" only targets eligible (non-excluded) apps.
                              const ids = apps.filter((a) => !a.excluded_from_apr).map((a) => a.id);
                              const allChecked = ids.length > 0 && ids.every((id) => selectedAppIds.has(id));
                              const someChecked = ids.some((id) => selectedAppIds.has(id));
                              return (
                                <div key={area} style={{ marginBottom: 6 }}>
                                  <label
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 10,
                                      padding: '6px 10px',
                                      background: '#eef2f7',
                                      borderRadius: 4,
                                      cursor: 'pointer',
                                      fontWeight: 600,
                                      fontSize: 11,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.05em',
                                      color: 'var(--apr-text-muted)',
                                      userSelect: 'none',
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={allChecked}
                                      ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                      onChange={() => toggleGroup(ids)}
                                    />
                                    {area}
                                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--apr-text-muted)' }}>
                                      {apps.length}
                                    </span>
                                  </label>
                                  {apps.map((app) => {
                                    const excluded = !!app.excluded_from_apr;
                                    return (
                                    <label
                                      key={app.id}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '6px 10px 6px 28px',
                                        cursor: excluded ? 'not-allowed' : 'pointer',
                                        fontSize: 13,
                                        color: excluded ? 'var(--apr-text-muted)' : 'var(--apr-text-strong)',
                                        borderRadius: 4,
                                        userSelect: 'none',
                                        opacity: excluded ? 0.8 : 1,
                                      }}
                                      onMouseEnter={(e) => { if (!excluded) (e.currentTarget as HTMLElement).style.background = '#ffffff'; }}
                                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!excluded && selectedAppIds.has(app.id)}
                                        disabled={excluded}
                                        onChange={() => { if (!excluded) toggleApp(app.id); }}
                                      />
                                      {app.application_id_code && (
                                        <span style={{ fontWeight: 600, color: excluded ? 'var(--apr-text-muted)' : 'var(--apr-text-strong)', marginRight: 8 }}>{app.application_id_code}</span>
                                      )}
                                      {app.name}
                                      {excluded && (
                                        <span style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1px 6px', marginLeft: 8, whiteSpace: 'nowrap' }}>NOT RELEVANT</span>
                                      )}
                                    </label>
                                    );
                                  })}
                                </div>
                              );
                            })
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Section 3: Context & Configuration ── */}
                <div className="rd-form-section" style={{ marginBottom: 0 }}>
                  <div className="rd-form-section-title">Context &amp; Configuration</div>
                  <p className="rd-form-help" style={{ margin: '0 0 14px' }}>
                    These inputs guide the agents. The framework is reference only — the agents are free
                    to recommend any disposition.
                  </p>

                  <div className="rd-form-group" style={{ marginBottom: 14 }}>
                    <label className="rd-label" htmlFor="client-name">
                      Client / company name
                      <InfoTip title="Client / company name">
                        <p style={{ marginTop: 0 }}>The name of the company being analysed (e.g. <strong>Meridien</strong>). The AI is told to refer to the client by this name and to <strong>never mention any other company</strong>, and it is printed on the exported PowerPoint, Word and PDF deliverables.</p>
                        <p style={{ marginBottom: 0 }}>Leave blank to keep the deliverables company-neutral.</p>
                      </InfoTip>
                    </label>
                    <input
                      id="client-name"
                      type="text"
                      placeholder="e.g. Meridien"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      style={{ ...RD_FIELD_STYLE, width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div className="rd-form-row">
                    <div className="rd-form-group" style={{ marginBottom: 0 }}>
                      <label className="rd-label" htmlFor="client-industry">
                        Client industry
                        <InfoTip title="Client industry">
                          <p style={{ marginTop: 0 }}>The agents use the client's industry to benchmark typical application portfolios, common vendors, and disposition patterns for that sector.</p>
                          <p style={{ marginBottom: 0 }}>It sets the <strong>comparison lens</strong> for the analysis. Although a client belongs to one industry, you can deliberately pick a <strong>different</strong> industry to analyse the portfolio through another sector's expectations.</p>
                        </InfoTip>
                      </label>
                      <select
                        id="client-industry"
                        value={clientIndustry}
                        onChange={(e) => setClientIndustry(e.target.value)}
                        style={RD_FIELD_STYLE}
                      >
                        <option value="">— Select —</option>
                        {INDUSTRIES.map((ind) => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                    </div>
                    <div className="rd-form-group" style={{ marginBottom: 0 }}>
                      <label className="rd-label" htmlFor="client-size">
                        Client size
                        <InfoTip title="Client size">
                          <p style={{ marginTop: 0, marginBottom: 0 }}>Calibrates the analysis to the organisation's scale — licensing volume, consolidation opportunity, and negotiating leverage. Larger sizes bias toward enterprise-agreement leverage and portfolio rationalisation; smaller sizes toward SaaS right-sizing and cost control.</p>
                        </InfoTip>
                      </label>
                      <select
                        id="client-size"
                        value={clientSize}
                        onChange={(e) => setClientSize(e.target.value)}
                        style={RD_FIELD_STYLE}
                      >
                        <option value="">— Select —</option>
                        <option value="Small">Small</option>
                        <option value="Mid-market">Mid-market</option>
                        <option value="Enterprise">Enterprise</option>
                        <option value="Global Enterprise">Global Enterprise</option>
                      </select>
                    </div>
                  </div>

                  <div className="rd-form-group" style={{ marginTop: 14, marginBottom: 0 }}>
                    <label className="rd-label" htmlFor="framework">
                      Framework (reference only)
                      <InfoTip title="Framework (reference only)">
                        <p style={{ marginTop: 0 }}>The selected framework is passed to the agents as a <strong>reference lens</strong> for mapping and labelling dispositions — it is <strong>not binding</strong>.</p>
                        <p style={{ marginBottom: 0 }}>The agents remain free to recommend any disposition regardless of the framework. Leave as <em>"No framework"</em> for a fully unconstrained analysis.</p>
                      </InfoTip>
                    </label>
                    <select
                      id="framework"
                      value={selectedFrameworkId}
                      onChange={(e) => handleFrameworkChange(e.target.value)}
                      style={RD_FIELD_STYLE}
                    >
                      <option value="">— No framework —</option>
                      {frameworks.map((fw) => (
                        <option key={fw.id} value={fw.id}>{fw.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ── Additional Analysis Instructions ── */}
                <div className="rd-form-section" style={{ marginTop: 16, marginBottom: 0 }}>
                  <div className="rd-form-section-title">
                    Additional Analysis Instructions
                    <InfoTip title="Additional Analysis Instructions">
                      <p style={{ marginTop: 0 }}>Instructions and context you want the AI to weigh that <strong>isn't captured by the framework Q&amp;A or the uploaded documents.</strong> The agents read this alongside the portfolio data when deciding dispositions — state anything you specifically want considered.</p>
                      <p style={{ margin: '0 0 4px' }}>Useful things to include:</p>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>Business criticality of specific applications</li>
                        <li>% of revenue an application supports</li>
                        <li>Upcoming M&amp;A, divestiture, or reorganisation</li>
                        <li>Regulatory / compliance constraints</li>
                        <li>Mandates (e.g. exit on-prem, budget freeze, preferred strategic vendor)</li>
                      </ul>
                      <p style={{ margin: '8px 0 4px' }}><strong>Major ERP direction</strong> — for a core ERP the analysis factors in vendor end-of-support (SAP ECC ends 2027 → S/4HANA; Oracle EBS → Fusion Cloud / OCI). Steer it here:</p>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>SAP S/4HANA approach: <strong>Greenfield</strong> (re-implement), <strong>Brownfield</strong> (in-place conversion), or <strong>Bluefield</strong> (selective data transition)</li>
                        <li>Oracle: re-implement on <strong>Fusion Cloud</strong> vs <strong>OCI lift-and-shift</strong></li>
                        <li>Target go-live / deadline, budget, or risk appetite</li>
                      </ul>
                    </InfoTip>
                  </div>
                  <p className="rd-form-help" style={{ margin: '0 0 8px' }}>
                    Anything you want the analysis to account for that isn't in the framework or the Knowledge Library. The more specific your instructions, the more tailored the result.
                  </p>
                  <textarea
                    rows={4}
                    placeholder="e.g. ERP is business-critical (supports ~70% of revenue); mandate to exit the on-prem data centre by Q4; prefer Microsoft as the strategic vendor…"
                    value={additionalComments}
                    onChange={(e) => setAdditionalComments(e.target.value)}
                    style={{ resize: 'vertical', padding: '10px 12px', width: '100%', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Error */}
                {formError && (
                  <div className="rd-alert rd-alert-danger" style={{ marginTop: 16 }}>
                    <i className="material-icons" style={{ fontSize: 18 }}>error_outline</i>
                    <span>{formError}</span>
                  </div>
                )}
              </div>

              <div className="rd-modal-footer">
                <button type="button" className="rd-btn rd-btn-ghost" onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="rd-btn rd-btn-primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <i className="material-icons rd-spin" style={{ fontSize: 16 }}>refresh</i>
                      Launching…
                    </>
                  ) : (
                    'Launch Run'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunAnalysis;
